import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { DocumentFilingService } from '../../services/document-filing/document-filing.service';
import { BatchResponse } from '@softdocs/capture-ui-libraries/types/softdocs-capture-ui-libraries';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import {
  Area,
  ContentApiService,
  DocumentType,
  KeyField
} from '../../services/content-api/content-api.service';
import { Select, SelectChangeEvent } from 'primeng/select';
import { TranslatePipe } from '@ngx-translate/core';
import { CurrencyPipe, NgClass } from '@angular/common';
import { Bind } from 'primeng/bind';
import { AutoComplete } from 'primeng/autocomplete';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { Textarea } from 'primeng/textarea';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { BatchService } from '../../services/batch/batch.service';
import { buildContentUrl } from '../../utils/documentUtils';
import { FieldValue } from '../../models/etrieveContent';
import { ToastService } from '../../services/toast-messages/toast-messages.service';
/**
 * Key field entry form for a single document. Lets the user pick an area and document type,
 * renders that type's key fields (including multi-value and lookup fields), autosaves edits,
 * and emits an export event once the form is valid.
 */
@Component({
  selector: 'app-key-fields',
  templateUrl: './key-fields.component.html',
  styleUrls: ['./key-fields.component.scss'],
  imports: [
    AutoComplete,
    Bind,
    Button,
    DatePicker,
    FormsModule,
    InputNumber,
    InputText,
    NgClass,
    ReactiveFormsModule,
    Select,
    Textarea,
    TranslatePipe
  ]
})
export class KeyFieldsComponent implements OnInit, OnDestroy {
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly docFilingService = inject(DocumentFilingService);
  private readonly docMngmtService = inject(DocumentManagementService);
  private readonly contentService = inject(ContentApiService);
  private readonly batchService = inject(BatchService);
  private readonly toastService = inject(ToastService);

  private tenantId: string = '';
  private autosaveTimeout: any;

  private formMetadataCache = new Map<string, {}>();
  private docTypesByAreaCache = new Map<string, DocumentType[]>();
  private selectedAreaForDocument = new Map<string, string>();
  private selectedDocTypeForDocument = new Map<string, DocumentType>();

  private terminalStates = ['failed', 'extraction failed', 'extraction prompt failed', 'extracted'];

  areaAndDocumentTypeRequired: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const area = group.get('area')?.value;
    const documentTypeCode = group.get('documentTypeCode')?.value;
    return area && documentTypeCode ? null : { areaAndDocumentTypeRequired: true };
  };

  multiValueRequiredValidator(key: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const entries = this.multiValueFields().get(key) || [];
      const isEmpty =
        (!control.value || control.value.toString().trim() === '') && entries.length === 0;
      return isEmpty ? { required: true } : null;
    };
  }

  readonly baseForm = {
    area: new FormControl(''),
    documentTypeCode: new FormControl('')
  };

  @Input() batch: BatchResponse = {} as BatchResponse;
  @Input() docAreas: Area[] = [];
  @Input() autoclassificationEnabled!: boolean;
  @Input() canFile!: boolean;
  @Input() showDeleteDocument!: boolean;
  @Input() extractionCompleteEvent!: Observable<any>;

  @Output() cancelClassificationEvent = new EventEmitter<DocumentType>();
  @Output() deleteDocumentEvent = new EventEmitter<void>();
  @Output() reExtractDocumentEvent = new EventEmitter<DocumentType>();
  @Output() exportDocumentEvent = new EventEmitter<any>();

  lookupSubject = new BehaviorSubject([]);
  activeForm = signal<FormGroup>(
    new FormGroup(this.baseForm, { validators: this.areaAndDocumentTypeRequired })
  );

  activeKeyfields = signal<KeyField[]>([]);
  activeAreaCode = signal<string>('');
  activeDoctype = signal<DocumentType>({} as DocumentType);
  filteredDocTypes = signal<DocumentType[]>([]);
  currentDocument = signal<any>({});
  multiValueFields = signal<Map<string, string[]>>(new Map<string, string[]>());
  dynamicUrl = signal<string>('');
  activeLookup = signal<any>([]);

  documentIsAvailable = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  isExporting = signal<boolean>(false);
  saveFailed = signal<boolean>(false);
  userHasModified = signal<boolean>(false);

  /**
   * Reacts to document selection changes in the filing service and reloads the form for the newly selected document.
   */
  constructor() {
    effect(() => {
      const selectedDocument = this.docFilingService.selectedDocument();
      if (
        selectedDocument &&
        (this.currentDocument().fileId != selectedDocument.fileId ||
          this.currentDocument().completed != selectedDocument.completed)
      ) {
        this.updateOnDocumentChange(selectedDocument);
        this.buildDynamicUrl();
      }
    });
  }

  /**
   * Loads the tenant ID and subscribes to extraction-complete and lookup events.
   */
  ngOnInit() {
    this.tenantId = localStorage.getItem('tenantId') || '';
    this.extractionCompleteEvent.subscribe((document: any) => {
      if (!!document.status) {
        this.updateOnDocumentChange(document);
      }
    });

    this.lookupSubject.subscribe(value => {
      this.activeLookup.set(value);
    });
  }

  /**
   * Cancels any pending autosave.
   */
  ngOnDestroy() {
    clearTimeout(this.autosaveTimeout);
  }

  /**
   * Loads the form for a document. Source priority: (1) saved metadata, (2) extracted data when
   * auto-classification is enabled, (3) the batch document's default area/type, (4) an empty form.
   * @param selectedDocument - The document to display.
   */
  async updateOnDocumentChange(selectedDocument: any | null) {
    this.currentDocument.set(selectedDocument);
    this.documentIsAvailable.set(!!selectedDocument.status);
    const batchDocument = await this.batchService.getBatchDocument(
      this.batch.batchId,
      selectedDocument.fileId
    );
    //attempt retrieve metadata
    const docMetadata: any =
      this.formMetadataCache.get(selectedDocument.fileId) ??
      (await this.docMngmtService.getDocumentContentMetadata(
        this.tenantId,
        selectedDocument.fileId
      ));

    //handle metadata and then end execution
    if (docMetadata) {
      const formData = docMetadata.data
        ? this.docMngmtService.parseMetadataToFormData(docMetadata)
        : docMetadata;
      //store it in cache so we don't re-query the db
      this.formMetadataCache.set(selectedDocument.fileId, formData);
      this.createFormFromData(formData);
      return;
    } else {
      this.formMetadataCache.set(selectedDocument.fileId, '');
    }

    //handle extracted data and then end execution
    const extractionData = selectedDocument.extractionData;
    const extractedDocTypeCode = extractionData?.tenant_doctype || extractionData?.ai_doctype;
    if (
      this.terminalStates.indexOf(selectedDocument?.extractionStatus) !== -1 &&
      extractedDocTypeCode &&
      this.autoclassificationEnabled
    ) {
      this.contentService.getDocumentTypeByCode(extractedDocTypeCode).subscribe(docType => {
        if (docType) {
          const areaCode = docType.areaCode;
          const extractedData = extractionData?.extraction_result?.standard_json_output || {};
          const formData = {
            area: areaCode,
            documentTypeCode: extractedDocTypeCode,
            ...extractedData
          };
          this.createFormFromData(formData);
          return;
        }
      });
    }

    if (batchDocument) {
      const defaultArea = batchDocument.defaultArea ?? null;
      const defaultDocumentType = batchDocument.defaultDocumentType ?? null;
      this.createFormFromData({ area: defaultArea, documentTypeCode: defaultDocumentType });
      return;
    }

    this.createFormFromData({});
    return;
  }

  /**
   * Sets the area and document type from form data and loads that area's document types (cached).
   * With no area, resets the form to its base controls.
   * @param formData - Values keyed by field code, including `area` and `documentTypeCode`.
   */
  private createFormFromData(formData: any) {
    const { area, documentTypeCode } = formData || {};
    if (area) {
      this.activeAreaCode.set(area);
      this.selectedAreaForDocument.set(this.currentDocument().fileId, area);
      const docTypes = this.docTypesByAreaCache.get(area);
      if (!docTypes) {
        this.contentService.getDocumentTypesByArea(area).subscribe(types => {
          this.filteredDocTypes.set(types || []);
          this.docTypesByAreaCache.set(this.activeAreaCode(), types || []);
          if (documentTypeCode) {
            this.updateFormWithKeyfields(documentTypeCode, formData);
          }
        });
      } else {
        this.filteredDocTypes.set(docTypes);
        this.updateFormWithKeyfields(documentTypeCode, formData);
      }
      this.activeForm().get('area')?.setValue(area);
    } else {
      //no area, no docType = reset the form back to basics
      this.removeKeyFieldsFromForm();
      this.activeAreaCode.set('');
      this.activeDoctype.set({} as DocumentType);
      this.activeForm().get('area')?.setValue('');
      this.activeForm().get('documentTypeCode')?.setValue('');
      this.selectedAreaForDocument.set(this.currentDocument().fileId, '');
    }
  }

  /**
   * Swaps the form's key fields for those of the given document type and prefills them.
   * @param documentTypeCode - Code of the document type to select within the filtered types.
   * @param formData - Initial values keyed by field code.
   */
  updateFormWithKeyfields(documentTypeCode: string, formData: any = {}) {
    this.removeKeyFieldsFromForm();
    const selectedDocType = this.filteredDocTypes()?.find(type => type.code === documentTypeCode);
    if (selectedDocType) {
      this.activeDoctype.set(selectedDocType);
      this.selectedDocTypeForDocument.set(this.currentDocument().fileId, selectedDocType);
      this.addKeyFieldsToForm(selectedDocType, formData);
    }
    this.activeForm().get('documentTypeCode')?.setValue(documentTypeCode);
  }

  /**
   * Applies validation rules to a single form control.
   * @param field - The key field definition containing validation rules and defaults.
   * @param formControl - the form control to add validation logic to
   */
  applyFieldRules(field: KeyField, formControl: FormControl): void {
    if (field.required) {
      if (field.multivalue) {
        const multivalueKey = `${this.activeAreaCode()}-${this.activeDoctype().code}-${field.code}`;
        formControl.addValidators(this.multiValueRequiredValidator(multivalueKey));
      } else {
        formControl.addValidators([Validators.required]);
      }
    }

    if (field.maxLength != undefined) {
      formControl.addValidators([Validators.maxLength(field.maxLength)]);
    }

    if (field.textPattern != undefined) {
      formControl.addValidators([Validators.pattern(field.textPattern)]);
    }

    if (field.minValue != undefined) {
      formControl.addValidators([Validators.min(field.minValue)]);
    }

    if (field.constraint && field.constraint.length > 0) {
      const allowedValues = field.constraint;
      const constraintValidator: ValidatorFn = (control: AbstractControl) => {
        if (!control.value) return null;
        return allowedValues.includes(control.value)
          ? null
          : { constraint: { allowedValues, actualValue: control.value } };
      };
      formControl.addValidators(constraintValidator);
    }
    formControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Applies default values to a single form control. `CurrentDate` defaults on Date fields resolve to today;
   * stray strings on numeric fields are cleared.
   * @param field - The key field definition containing validation rules and defaults.
   * @param formControl - The form control to set the default on.
   */
  setDefaultAndEmptyValues(field: KeyField, formControl: FormControl): any {
    if (field.default && (formControl.value == null || formControl.value == '')) {
      if (field.dataType === 'Date' && field.default === 'CurrentDate') {
        formControl.setValue(new Date());
      } else {
        formControl.setValue(field.default);
      }
    } else if (
      typeof formControl.value == 'string' &&
      (field.dataType == 'Number' || field.dataType == 'Decimal' || field.dataType == 'Money')
    ) {
      formControl.setValue(null);
    }
  }

  /**
   * Handles area selection: clears the document type and its key fields, then loads the area's document types.
   * @param $event - Select change event whose value is the area code.
   */
  onAreaUpdate($event: SelectChangeEvent) {
    const selectedAreaCode = $event.value;
    this.activeAreaCode.set(selectedAreaCode);
    this.selectedAreaForDocument.set(this.currentDocument().fileId, selectedAreaCode);

    //reset form values as needed
    this.removeKeyFieldsFromForm();
    this.activeForm().get('documentTypeCode')?.setValue('');

    //set new values
    const docTypes = this.docTypesByAreaCache.get(selectedAreaCode);
    if (!docTypes) {
      this.contentService.getDocumentTypesByArea(selectedAreaCode).subscribe(types => {
        this.filteredDocTypes.set(types || []);
        this.docTypesByAreaCache.set(this.activeAreaCode(), types || []);
      });
    } else {
      this.filteredDocTypes.set(docTypes);
    }
  }

  /**
   * Handles document type selection: adds its key fields and, if auto-classification is on,
   * requests re-extraction for the new type.
   * @param $event - Select change event whose value is the document type code.
   */
  onDocTypeUpdate($event: SelectChangeEvent) {
    const selectedDocTypeCode = $event.value;
    const docTypes = this.docTypesByAreaCache.get(this.activeAreaCode());
    const selectedDocType = docTypes?.find(type => type.code === selectedDocTypeCode);
    if (selectedDocType) {
      this.activeDoctype.set(selectedDocType);
      this.selectedDocTypeForDocument.set(this.currentDocument().fileId, selectedDocType);
      this.addKeyFieldsToForm(selectedDocType);

      if (this.autoclassificationEnabled) {
        this.reExtractDocumentEvent.emit(selectedDocType);
        this.documentIsAvailable.set(false);
      }
    }
  }

  /**
   * Adds a control for each key field of the document type, applying initial values, defaults and validation.
   * @param docType - Document type whose key fields to add.
   * @param formData - Optional initial values keyed by field code.
   */
  addKeyFieldsToForm(docType: DocumentType, formData?: any) {
    docType.keyfields.forEach(keyfield => {
      let startingValue = formData ? formData[keyfield.code] : null;
      if (keyfield.dataType == 'Date' && startingValue) {
        startingValue = new Date(startingValue);
      }
      if (keyfield.dataType == 'Lookup' && formData) {
        this.setInitialLookup(keyfield, formData);
      }
      if (
        startingValue &&
        (keyfield.dataType == 'Number' ||
          keyfield.dataType == 'Decimal' ||
          keyfield.dataType == 'Money')
      ) {
        let strippedString = startingValue.replace(/[^0-9.]/g, '');
        startingValue = !isNaN(+strippedString) ? +strippedString : null;
      }
      if (keyfield.multivalue && startingValue) {
        let values = startingValue.split(',');
        let multiValueKey = `${this.activeAreaCode()}-${this.activeDoctype().code}-${keyfield.code}`;
        this.multiValueFields().set(multiValueKey, values);
        startingValue = '';
      }
      const newFormControl = new FormControl(startingValue);
      this.setDefaultAndEmptyValues(keyfield, newFormControl);
      this.applyFieldRules(keyfield, newFormControl);
      this.activeForm().addControl(keyfield.code, newFormControl);
    });
    this.activeKeyfields.set(docType.keyfields);
    this.activeForm().markAsPristine();
  }

  /**
   * Runs a lookup search for a lookup field as the user types.
   * @param $event - Autocomplete event carrying the `query` string.
   * @param keyfield - The lookup key field being searched.
   */
  searchLookup($event: any, keyfield: KeyField) {
    const query = $event.query || '';
    this.lookupUsers(keyfield.code, keyfield.lookupType?.code ?? '', query);
  }

  /**
   * Queries lookup results and publishes them to {@link lookupSubject}; publishes `[]` on error.
   * @param keyfieldCode - Code of the field to populate.
   * @param lookupCode - Code of the lookup type.
   * @param query - Search text.
   * @param isInitialLookup - If true, the first result is set as the field's value.
   */
  lookupUsers(
    keyfieldCode: string,
    lookupCode: string,
    query: string,
    isInitialLookup: boolean = false
  ) {
    this.contentService.lookupUser(query, lookupCode).subscribe(
      response => {
        this.lookupSubject.next(response);
        if (isInitialLookup && response.length >= 1) {
          this.activeForm()?.get(keyfieldCode)?.setValue(response[0]);
        }
      },
      error => {
        console.error('[KeyFields] Lookup error:', error);
        this.lookupSubject.next([]);
      }
    );
  }

  /**
   * Prefills a lookup field from extracted naming-field values (`fieldCode.namingFieldCode`), only for
   * documents with completed extraction.
   * @param keyfield - The lookup key field.
   * @param formData - Values keyed by field code.
   */
  setInitialLookup(keyfield: KeyField, formData: any) {
    const hasExtractionData =
      this.currentDocument()?.extractionData?.extraction_result &&
      this.currentDocument()?.extractionData?.record_status === 'extracted';
    const allNamingFields = keyfield.lookupType?.namingFields || [];
    const lookupSearchTerm = allNamingFields
      .map(namingField => {
        const nestedKey = `${keyfield.code}.${namingField.fieldCode}`;
        const formKey = Object.keys(formData).find((f: any) => f === nestedKey);
        return formKey ? formData[formKey] : '';
      })
      .join(' ')
      .trim();

    if (lookupSearchTerm && keyfield.lookupType && hasExtractionData) {
      this.lookupUsers(keyfield.code, keyfield.lookupType.code, lookupSearchTerm, true);
    }
  }

  /**
   * Removes all key field controls, leaving the base controls, and clears the modified flag.
   */
  removeKeyFieldsFromForm() {
    this.activeKeyfields().forEach(keyfield => {
      this.activeForm().removeControl(keyfield.code);
    });
    this.activeKeyfields.set([]);
    this.userHasModified.set(false);
  }

  /**
   * Handles any field edit: snapshots the form to the filing service, flags the form modified,
   * and schedules a debounced (1200 ms) autosave.
   */
  onFieldChange() {
    const snapshot = this.activeForm().getRawValue();
    this.docFilingService.updateFormSnapshot(this.currentDocument().fileId, snapshot);

    this.userHasModified.set(true);

    clearTimeout(this.autosaveTimeout);
    this.autosaveTimeout = setTimeout(() => {
      this.saveChanges(snapshot);
      this.formMetadataCache.set(this.currentDocument().fileId, snapshot);
    }, 1200);
  }

  /**
   * Moves the field's current text input into its multi-value list and clears the input.
   * @param keyfieldCode - Code of the multi-value field.
   */
  updateMultivalueField(keyfieldCode: string) {
    const formControl = this.activeForm().get(keyfieldCode);
    const multiValueKey = `${this.activeAreaCode()}-${this.activeDoctype().code}-${keyfieldCode}`;
    if (formControl?.value) {
      const currentValues = this.multiValueFields().get(multiValueKey) || [];
      currentValues.push(formControl.value);
      this.multiValueFields().set(multiValueKey, currentValues);
    }
    formControl?.setValue('');
    this.onFieldChange();
  }

  /**
   * Removes one entry from a multi-value field's list.
   * @param keyfieldCode - Code of the multi-value field.
   * @param index - Index of the entry to remove.
   */
  removeMultipleValue(keyfieldCode: string, index: number) {
    const multiValueKey = `${this.activeAreaCode()}-${this.activeDoctype().code}-${keyfieldCode}`;
    const currentValues = this.multiValueFields().get(multiValueKey) || [];
    currentValues.splice(index, 1);
    this.multiValueFields().set(multiValueKey, currentValues);
    this.onFieldChange();
  }

  /**
   * Persists form data to the backend, serializing multi-value fields as comma-separated strings.
   * Skipped without a tenant ID or document. Sets {@link isSaving} and {@link saveFailed}.
   * @param formData The current form data to save.
   * @throws Rethrows the save error after setting {@link saveFailed}.
   */
  private async saveChanges(formData: any): Promise<void> {
    this.activeKeyfields().forEach(kf => {
      if (kf.multivalue) {
        const multiValueKey = `${this.activeAreaCode()}-${this.activeDoctype().code}-${kf.code}`;
        const currentValues = this.multiValueFields().get(multiValueKey) || [];
        formData[kf.code] = currentValues.join(',');
      }
    });

    if (!this.tenantId || !this.currentDocument().fileId) return;

    this.isSaving.set(true);
    this.saveFailed.set(false);

    try {
      await this.docMngmtService.saveKeyFieldsMetadata(
        this.tenantId,
        this.currentDocument().fileId,
        formData
      );
      this.docFilingService.markSnapshotClean(this.currentDocument().fileId);
      this.activeForm().markAsPristine();
    } catch (error) {
      console.error('[KeyFieldsComponent] Failed to save key fields:', error);
      this.saveFailed.set(true);
      throw error;
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Formats the input's value as USD currency (e.g. `$1,234.50`).
   * @param $event - Event whose target is the input element.
   */
  transformToCurrency($event: any): void {
    $event.target.value =
      this.currencyPipe.transform($event.target.value, 'USD', 'symbol', '1.2-2') ?? '';
  }

  /**
   * Generates user-friendly error messages for the specified key field based on its validation errors.
   * @param keyFieldCode The code of the key field to generate error messages for.
   * @returns The message for the first matching error, or an empty string if the field is valid.
   */
  errorText(keyFieldCode: string): string {
    const errors = this.activeForm().controls[keyFieldCode]?.errors;
    if (!errors) return '';
    if (errors['required']) return 'Required';
    if (errors['maxlength']) return 'Maximum length exceeded';
    if (errors['pattern']) return 'Invalid format';
    if (errors['min']) return `Minimum value is ${errors['min'].min}`;
    return '';
  }

  /**
   * Emits {@link cancelClassificationEvent} with the active document type.
   */
  cancelClassification() {
    this.cancelClassificationEvent.emit(this.activeDoctype());
  }

  /**
   * Sets {@link this.dynamicUrl} to the Content link for the current document. Does nothing unless the
   * document is completed and has an `ecmDocId`, or if the base Content URL is missing from localStorage.
   */
  buildDynamicUrl(): void {
    if (
      !this.currentDocument()?.completed ||
      typeof this.currentDocument().ecmDocId == 'undefined'
    ) {
      return;
    }

    const documentUrl = buildContentUrl(this.currentDocument().ecmDocId);
    if (!documentUrl) {
      console.error('[KeyFieldsComponent] Content URL not found in localStorage');
      return;
    }

    this.dynamicUrl.set(documentUrl);
  }

  /**
   * Validates the form and, if valid, emits {@link this.exportDocumentEvent}; otherwise shows an error toast.
   */
  exportDocument() {
    if (!this.activeForm()?.valid) {
      console.error('Form is not valid, cannot upload document.');
      this.toastService.error('error', 'validationError', 10000);
      return;
    }
    this.isExporting.set(true);
    const exportForm = this.buildKeyFieldsForExport(
      this.activeForm().getRawValue(),
      this.activeDoctype()
    );
    this.exportDocumentEvent.emit({
      formData: exportForm,
      areaCode: this.activeAreaCode(),
      docTypeCode: this.activeDoctype().code
    });
  }

  /**
   * Converts form values into export field values. Skips empty values and the base controls; expands
   * arrays into one entry per item; and for lookup fields emits only the unique-identifier fields,
   * tagged with `parentFieldCode`.
   * @param formData - Form values.
   * @param documentType - Document type, used to resolve lookup field definitions.
   * @returns The field values to export.
   */
  private buildKeyFieldsForExport = (formData: any, documentType: DocumentType): FieldValue[] => {
    const fieldValues: FieldValue[] = [];

    const uniqueLookupIdentifier: Record<string, string> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (key !== 'documentTypeCode' && key !== 'area' && value) {
        if (Array.isArray(value)) {
          // If the value is an array, push each item as a separate fieldValue
          value.forEach(item => {
            fieldValues.push({
              fieldCode: key,
              value: item
            });
          });
        } else if (value && typeof value === 'object' && 'lookupTypeCode' in value) {
          // If the field is a lookup field value, build field values only for lookup unique identifier fields
          const lookupFieldDefinition = documentType.keyfields.find(field => field.code === key);
          if (lookupFieldDefinition && lookupFieldDefinition.lookupType) {
            lookupFieldDefinition.lookupType.keyfields.forEach(field => {
              if (field.isUniqueIdentifier) {
                const fieldValue = (value as any).fieldValues.find(
                  (fv: any) => fv.fieldCode === field.code
                );
                if (fieldValue && !uniqueLookupIdentifier[lookupFieldDefinition.code]) {
                  fieldValues.push({
                    fieldCode: field.code,
                    value: fieldValue.value,
                    parentFieldCode: lookupFieldDefinition.code
                  });
                  uniqueLookupIdentifier[lookupFieldDefinition.code] = fieldValue.value;
                }
              }
            });
          }
        } else {
          // If the value is not an array and not a lookup field, push it as is
          fieldValues.push({
            fieldCode: key,
            value: value
          });
        }
      }
    }

    return fieldValues;
  };

  /** Resets {@link this.isExporting} after the export completes (success or failure). */
  onExportComplete(): void {
    this.isExporting.set(false);
    this.buildDynamicUrl();
  }
}
